import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDate } from '../lib/format'

/**
 * Country-tiered price book (part 144).
 *
 * Every amount crosses the wire as an integer in the currency's minor unit:
 * satang for THB, cents for SGD, dong for VND. The browser parses major units
 * with string arithmetic and never turns a price into a float.
 */

interface PriceRow {
  id: string
  plan_key: string
  country: string
  currency: string
  monthly_satang: number
  per_doc_satang: number
  tax_bp: number
  effective_on: string
}

interface PriceQuote {
  plan_key: string
  requested_country: string
  country: string
  currency: string
  docs: number
  monthly_satang: number
  per_doc_satang: number
  docs_satang: number
  subtotal_satang: number
  tax_bp: number
  tax_satang: number
  total_satang: number
  effective_on: string
  fallback: boolean
}

const BOOK_KEY = ['pricing-book'] as const
const QUOTE_KEY = ['pricing-quote'] as const
const KNOWN_PLANS = ['starter', 'growth', 'enterprise']
const EMPTY_ROWS: PriceRow[] = []

const COUNTRIES = [
  { code: 'TH', name: 'ไทย', currency: 'THB' },
  { code: 'SG', name: 'สิงคโปร์', currency: 'SGD' },
  { code: 'VN', name: 'เวียดนาม', currency: 'VND' },
  { code: 'US', name: 'สหรัฐอเมริกา', currency: 'USD' },
  { code: 'GB', name: 'สหราชอาณาจักร', currency: 'GBP' },
  { code: 'AU', name: 'ออสเตรเลีย', currency: 'AUD' },
]

/** Currencies whose minor unit is the whole unit — no /100 on display. */
const ZERO_DECIMAL = new Set(['VND', 'JPY', 'KRW'])

function currencyFor(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.currency ?? 'THB'
}

function today(): string {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** "1,900.00" + THB -> 190000; "1,450,000" + VND -> 1450000. */
function parseAmountSatang(raw: string, currency: string): number | null {
  const decimals = ZERO_DECIMAL.has(currency) ? 0 : 2
  const s = raw.trim().replace(/,/g, '')
  const re = decimals === 0 ? /^\d+$/ : /^\d+(\.\d{0,2})?$/
  if (!re.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  const major = Number(whole)
  const fracValue = decimals === 0 ? 0 : Number((frac + '00').slice(0, 2))
  const value = major * (decimals === 0 ? 1 : 100) + fracValue
  return Number.isSafeInteger(value) && value > 0 ? value : null
}

/** "7" -> 700; "8.25" -> 825. Basis points, capped at 30%. */
function parseTaxBP(raw: string): number | null {
  const s = raw.trim()
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  const bp = Number(whole) * 100 + Number((frac + '00').slice(0, 2))
  return Number.isSafeInteger(bp) && bp >= 0 && bp <= 3000 ? bp : null
}

function taxPercent(bp: number): string {
  return `${(bp / 100).toLocaleString('th-TH', { maximumFractionDigits: 2 })}%`
}

/** Integer minor units -> "SGD 129.71" / "฿8,667.00" / "VND 1,595,000". */
function amount(satang: number, currency: string): string {
  const zero = ZERO_DECIMAL.has(currency)
  const major = satang / (zero ? 1 : 100)
  const text = major.toLocaleString('th-TH', {
    minimumFractionDigits: zero ? 0 : 2,
    maximumFractionDigits: zero ? 0 : 2,
  })
  return currency === 'THB' ? `฿${text}` : `${currency} ${text}`
}

function planLabel(plan: string): string {
  const known = KNOWN_PLANS.find((p) => p === plan)
  return known ? known.charAt(0).toUpperCase() + known.slice(1) : plan
}

export function Pricing() {
  const { me } = useAuth()
  const canManage = me?.role === 'owner' || me?.role === 'admin'
  const qc = useQueryClient()

  const book = useQuery({
    queryKey: BOOK_KEY,
    queryFn: () => list<PriceRow>('/v1/pricing/book'),
    staleTime: 30_000,
  })
  const rows = book.data ?? EMPTY_ROWS

  const planOptions = useMemo(() => {
    const present = new Set(rows.map((r) => r.plan_key))
    const known = KNOWN_PLANS.filter((p) => present.has(p))
    const extra = Array.from(present).filter((p) => !KNOWN_PLANS.includes(p)).sort()
    return known.length > 0 || extra.length > 0 ? [...known, ...extra] : KNOWN_PLANS
  }, [rows])

  const bookCountries = useMemo(
    () => Array.from(new Set(rows.map((r) => r.country))).sort(),
    [rows],
  )

  // ---------------------------------------------------------------- quote
  const [quotePlan, setQuotePlan] = useState('starter')
  const [quoteCountry, setQuoteCountry] = useState('SG')
  const [docs, setDocs] = useState('100')
  const activePlan = planOptions.includes(quotePlan) ? quotePlan : (planOptions[0] ?? '')
  const docsValue = /^\d+$/.test(docs.trim()) && Number(docs) <= 1_000_000 ? Number(docs) : null

  const quote = useQuery({
    queryKey: [...QUOTE_KEY, activePlan, quoteCountry, docsValue],
    queryFn: () =>
      api<PriceQuote>(
        `/v1/pricing/quote?plan=${encodeURIComponent(activePlan)}&country=${encodeURIComponent(quoteCountry)}&docs=${docsValue ?? 0}`,
      ),
    enabled: activePlan !== '' && docsValue !== null,
    retry: false,
    staleTime: 10_000,
  })

  // ------------------------------------------------------------- add price
  const [planKey, setPlanKey] = useState('starter')
  const [country, setCountry] = useState('TH')
  const [currency, setCurrency] = useState('THB')
  const [monthly, setMonthly] = useState('1,900.00')
  const [perDoc, setPerDoc] = useState('3.00')
  const [tax, setTax] = useState('7')
  const [effectiveOn, setEffectiveOn] = useState(today())

  const monthlySatang = parseAmountSatang(monthly, currency)
  const perDocSatang = parseAmountSatang(perDoc, currency)
  const taxBP = parseTaxBP(tax)
  const formValid = monthlySatang !== null && perDocSatang !== null && taxBP !== null

  function pickCountry(code: string) {
    setCountry(code)
    if (COUNTRIES.some((c) => c.code === code)) setCurrency(currencyFor(code))
  }

  const save = useMutation({
    mutationFn: () =>
      apiWrite<PriceRow>('/v1/pricing/book', 'POST', {
        plan_key: planKey,
        country,
        currency,
        monthly_satang: monthlySatang,
        per_doc_satang: perDocSatang,
        tax_bp: taxBP,
        effective_on: effectiveOn,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BOOK_KEY })
      void qc.invalidateQueries({ queryKey: QUOTE_KEY })
    },
  })

  const q = quote.data

  return (
    <>
      <PageHead
        title="สมุดราคาตามประเทศ"
        sub="ราคาแยกตามประเทศและสกุลเงิน · จำนวนเต็มหน่วยย่อย · ภาษีคำนวณด้วย basis points"
        actions={<span className="chip">{rows.length} ราคา · {bookCountries.length} ประเทศ</span>}
      />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="row">
            <span className="card-label">ราคาทั้งหมด</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              ราคาล่าสุดของแต่ละวันมีผลตั้งแต่วันที่มีผลเป็นต้นไป
            </span>
          </div>

          {book.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={200} /></div>
          ) : book.isError ? (
            <div className="form-error" style={{ marginTop: 14 }}>
              โหลดสมุดราคาไม่สำเร็จ — {book.error instanceof Error ? book.error.message : 'ลองรีเฟรชอีกครั้ง'}
            </div>
          ) : rows.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty title="ยังไม่มีราคาในสมุด" body="เพิ่มราคาแรกทางด้านขวา แล้วเครื่องคำนวณจึงจะเสนอราคาได้" />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>ประเทศ</th><th>แพ็กเกจ</th><th>สกุลเงิน</th>
                    <th>รายเดือน</th><th>ต่อเอกสาร</th><th>ภาษี</th><th>มีผลวันที่</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ cursor: 'default' }}>
                      <td><span className="chip">{r.country}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{planLabel(r.plan_key)}</td>
                      <td className="mono">{r.currency}</td>
                      <td className="num tnum">{amount(r.monthly_satang, r.currency)}</td>
                      <td className="num tnum">{amount(r.per_doc_satang, r.currency)}</td>
                      <td className="num tnum dim">{taxPercent(r.tax_bp)}</td>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDate(r.effective_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">เพิ่มหรือแก้ไขราคา</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            วันที่มีผลซ้ำกับแถวเดิมจะแก้ไขราคาของวันนั้น ไม่สร้างแถวซ้อน
          </div>
          <form
            style={{ marginTop: 12 }}
            onSubmit={(e) => {
              e.preventDefault()
              if (canManage && formValid) save.mutate()
            }}
          >
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="price-plan">แพ็กเกจ</label>
                <select id="price-plan" className="input" value={planKey} disabled={!canManage}
                  onChange={(e) => setPlanKey(e.target.value)}>
                  {planOptions.map((p) => <option key={p} value={p}>{planLabel(p)}</option>)}
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="price-country">ประเทศ (ISO-2)</label>
                <select id="price-country" className="input" value={country} disabled={!canManage}
                  onChange={(e) => pickCountry(e.target.value)}>
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="price-currency">สกุลเงิน</label>
                <input id="price-currency" className="input mono" maxLength={3} value={currency}
                  disabled={!canManage}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="price-monthly">รายเดือน ({currency})</label>
                <input id="price-monthly" className="input mono" inputMode="decimal"
                  placeholder={ZERO_DECIMAL.has(currency) ? '1,450,000' : '1,900.00'}
                  value={monthly} disabled={!canManage} onChange={(e) => setMonthly(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="price-perdoc">ต่อเอกสาร ({currency})</label>
                <input id="price-perdoc" className="input mono" inputMode="decimal"
                  placeholder={ZERO_DECIMAL.has(currency) ? '2,500' : '3.00'}
                  value={perDoc} disabled={!canManage} onChange={(e) => setPerDoc(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="price-tax">ภาษี (%)</label>
                <input id="price-tax" className="input mono" inputMode="decimal" placeholder="7"
                  value={tax} disabled={!canManage} onChange={(e) => setTax(e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="price-effective">วันที่มีผล</label>
              <input id="price-effective" className="input" type="date" value={effectiveOn}
                disabled={!canManage} onChange={(e) => setEffectiveOn(e.target.value)} />
            </div>
            <button className="btn btn-primary" type="submit"
              disabled={!canManage || save.isPending || !formValid} style={{ width: '100%' }}>
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึกราคา'}
            </button>
          </form>
          {!canManage ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              เฉพาะเจ้าของและผู้ดูแลระบบเท่านั้นที่แก้ไขสมุดราคาได้
            </div>
          ) : null}
          {canManage && !formValid ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              ตรวจสอบจำนวนเงินและอัตราภาษี — ภาษีต้องอยู่ระหว่าง 0 ถึง 30%
            </div>
          ) : null}
          {save.isError ? (
            <div className="form-error" style={{ marginTop: 10 }}>
              {save.error instanceof Error ? save.error.message : 'บันทึกราคาไม่สำเร็จ'}
            </div>
          ) : null}
          {save.isSuccess ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              บันทึกแล้ว · {save.data.country} {planLabel(save.data.plan_key)} · มีผล {thaiDate(save.data.effective_on)}
            </div>
          ) : null}
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row">
          <span className="card-label">เครื่องคำนวณใบเสนอราคา</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            ประเทศที่ยังไม่มีราคาจะใช้ราคาเริ่มต้นของไทย (TH)
          </span>
        </div>

        <div className="row" style={{ gap: 10, marginTop: 14, alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="quote-plan">แพ็กเกจ</label>
            <select id="quote-plan" className="input" value={activePlan}
              onChange={(e) => setQuotePlan(e.target.value)}>
              {planOptions.map((p) => <option key={p} value={p}>{planLabel(p)}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="quote-country">ประเทศ</label>
            <select id="quote-country" className="input" value={quoteCountry}
              onChange={(e) => setQuoteCountry(e.target.value)}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="quote-docs">จำนวนเอกสาร</label>
            <input id="quote-docs" className="input mono" inputMode="numeric" value={docs}
              onChange={(e) => setDocs(e.target.value)} />
          </div>
        </div>

        {docsValue === null ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            จำนวนเอกสารต้องเป็นจำนวนเต็มตั้งแต่ 0 ถึง 1,000,000
          </div>
        ) : quote.isLoading ? (
          <div style={{ marginTop: 14 }}><Skel h={140} /></div>
        ) : quote.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            ยังไม่มีราคาที่ใช้ได้สำหรับแพ็กเกจนี้ — {quote.error instanceof Error ? quote.error.message : 'เพิ่มราคาก่อน'}
          </div>
        ) : q ? (
          <div style={{ marginTop: 16 }}>
            <div className="row">
              <span className="chip gold">{planLabel(q.plan_key)}</span>
              <span className={`chip${q.fallback ? '' : ' gold'}`}>
                {q.fallback ? `ใช้ราคาเริ่มต้น ${q.country}` : `ราคาเฉพาะ ${q.country}`}
              </span>
              <span className="mono dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                {q.currency} · มีผล {thaiDate(q.effective_on)}
              </span>
            </div>

            <dl className="kv" style={{ marginTop: 14, gridTemplateColumns: '1fr auto' }}>
              <dt>ค่าบริการรายเดือน</dt>
              <dd className="tnum" style={{ textAlign: 'right' }}>{amount(q.monthly_satang, q.currency)}</dd>
              <dt>ค่าเอกสาร {q.docs.toLocaleString('th-TH')} ฉบับ × {amount(q.per_doc_satang, q.currency)}</dt>
              <dd className="tnum" style={{ textAlign: 'right' }}>{amount(q.docs_satang, q.currency)}</dd>
              <dt>ยอดก่อนภาษี</dt>
              <dd className="tnum" style={{ textAlign: 'right' }}>{amount(q.subtotal_satang, q.currency)}</dd>
              <dt>ภาษี {taxPercent(q.tax_bp)}</dt>
              <dd className="tnum" style={{ textAlign: 'right' }}>{amount(q.tax_satang, q.currency)}</dd>
            </dl>
            <div className="rule" style={{ margin: '14px 0' }} />
            <div className="row">
              <span className="dim" style={{ fontSize: 12 }}>รวมทั้งสิ้น</span>
              <span className="display-m tnum" style={{ marginLeft: 'auto' }}>
                {amount(q.total_satang, q.currency)}
              </span>
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.9 }}>
              จำนวนเต็ม {q.total_satang.toLocaleString('th-TH')} หน่วยย่อย
              {q.fallback
                ? ` · ประเทศ ${q.requested_country} ยังไม่มีราคาเฉพาะ จึงใช้แถวเริ่มต้นของ ${q.country} (${q.currency})`
                : ` · ใช้ราคาเฉพาะประเทศ ${q.country}`}
            </div>
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">วิธีคิดราคา</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>แต่ละแถวคือราคาของแพ็กเกจหนึ่งในประเทศหนึ่ง มีผลตั้งแต่วันที่ระบุเป็นต้นไป</li>
          <li>เลือกแถวที่มีวันที่มีผลใหม่ที่สุดแต่ไม่เกินวันที่ขอราคา — ราคาในอนาคตจะไม่ถูกใช้ก่อนเวลา</li>
          <li>ประเทศที่มีราคาของตัวเองจะชนะเสมอ แม้ราคาเริ่มต้นของไทยจะใหม่กว่า</li>
          <li>ถ้าประเทศนั้นยังไม่มีราคาที่มีผล ระบบจะถอยไปใช้ราคาเริ่มต้นของไทย (TH) และแจ้งให้ทราบ</li>
          <li>ยอดทั้งหมดเป็นจำนวนเต็มหน่วยย่อย ภาษีคิดจากยอดก่อนภาษีและปัดครึ่งขึ้นเพียงครั้งเดียว</li>
        </ul>
      </div>
    </>
  )
}
